/**
 * Resource detail screen (DNKADM-0054 U2) — one `v2_resource` with the
 * **location-tabbed weekly schedule** editor and the **date-exceptions** panel (handoff
 * §5.7). Routed at `/resources/:resourceId` under `RequireAdmin`. Mirrors `StaffDetail`
 * (DNKADM-0053) — resources have no appointment caps, so that panel is absent; capacity
 * is shown in the header.
 *
 * Loads the resource (`getResource`), its assigned locations (`getResourceLocations` →
 * resolved to the active owned `listLocations`), and its date exceptions. Schedules are
 * authored **per (resource, location)**, so a **tab per assigned location** hosts that
 * location's weekly grid (Sun→Sat) — the schedule resource refetches when the active tab
 * (location) changes. A resource with no assigned locations shows a clear note (assign a
 * location via Edit details first). A load failure shows error + Retry; a missing
 * resource (`404`) shows a not-found note with a link back to the list.
 *
 * **Base-field editing (DNKADM-0286).** The header hosts an "Edit details" button that
 * opens the existing {@link ResourceDialog} (name/capacity/description/assigned
 * locations) against the loaded record — the detail page is the single place to view
 * AND edit; the list's quick-edit trigger is retired. A save refetches the record and
 * the assigned locations (the schedule tabs follow).
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Link as MuiLink,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import {
  getResource,
  getResourceLocations,
  getResourceSchedule,
  listResourceDateExceptions,
  type V2Resource,
} from '@/api/resources';
import { listLocations, type V2Location } from '@/api/locations';
import { ResourceScheduleGrid } from './ResourceScheduleGrid';
import { ResourceDateExceptionsPanel } from './ResourceDateExceptionsPanel';
import { ResourceDialog } from './ResourceDialog';
import { ScreenError } from '@/components/ScreenError';

/** Display name for a resource (its name, falling back to the id). */
function resourceName(resource: V2Resource): string {
  return resource.name.length > 0 ? resource.name : resource.id;
}

export function ResourceDetail() {
  const { resourceId = '' } = useParams<{ resourceId: string }>();
  const showToast = useToast();

  const resource = useResource(
    useCallback(() => getResource(resourceId), [resourceId]),
    [resourceId],
  );

  // Resolve the resource's assigned locations to full V2Location rows (for tab labels
  // and the exception location options). A failure degrades to no locations.
  const assigned = useResource(
    useCallback(async (): Promise<V2Location[]> => {
      const [ids, all] = await Promise.all([
        getResourceLocations(resourceId),
        listLocations({ status: 'active' }),
      ]);
      const order = new Map(all.map((l, i) => [l.id, i] as const));
      return all
        .filter((l) => ids.includes(l.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [resourceId]),
    [resourceId],
  );

  const exceptions = useResource(
    useCallback(() => listResourceDateExceptions(resourceId), [resourceId]),
    [resourceId],
  );

  // The active schedule tab = the selected assigned location id.
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const assignedData = assigned.data;
  const assignedLocations = useMemo(() => assignedData ?? [], [assignedData]);
  const effectiveLocationId = useMemo(() => {
    if (activeLocationId !== null && assignedLocations.some((l) => l.id === activeLocationId)) {
      return activeLocationId;
    }
    return assignedLocations[0]?.id ?? null;
  }, [activeLocationId, assignedLocations]);

  // The schedule for the active (resource, location); refetches when the tab changes.
  const schedule = useResource(
    useCallback(
      () =>
        effectiveLocationId === null
          ? Promise.resolve(null)
          : getResourceSchedule(resourceId, effectiveLocationId),
      [resourceId, effectiveLocationId],
    ),
    [resourceId, effectiveLocationId],
  );

  // Base-field editing in-context (DNKADM-0286): the same dialog the list's Add uses,
  // seeded with the loaded record; a save refetches the record + assigned locations.
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/resources"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All resources
      </MuiLink>

      {resource.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading resource"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {resource.status === 'error' && (
        <ScreenError
          error={resource.error}
          what="the resource"
          onRetry={resource.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That resource wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/resources">
                Back to resources
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {resource.status === 'ready' && resource.data && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h6" component="h1">
              {resourceName(resource.data)}
            </Typography>
            <Chip
              size="small"
              label={resource.data.isActive ? 'Active' : 'Inactive'}
              color={resource.data.isActive ? 'success' : 'default'}
              variant={resource.data.isActive ? 'filled' : 'outlined'}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="edit" />}
              onClick={() => setEditOpen(true)}
            >
              Edit details
            </Button>
          </Stack>
          <Stack spacing={0.25} sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Capacity: {resource.data.capacity} simultaneous reservation
              {resource.data.capacity === 1 ? '' : 's'}
            </Typography>
            {resource.data.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {resource.data.description}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* ---- Weekly schedule (location-tabbed) ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Weekly schedule
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Set the weekly availability for each location this resource is at, in that
            location’s local time.
          </Typography>

          {assigned.status === 'loading' && (
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3, maxWidth: 640 }} />
          )}

          {assigned.status === 'ready' && assignedLocations.length === 0 && (
            <Alert severity="info" sx={{ maxWidth: 620, mb: 4 }}>
              This resource isn’t assigned to any active locations yet. Assign it to a
              location (Edit details, above) to set a schedule.
            </Alert>
          )}

          {assignedLocations.length > 0 && effectiveLocationId !== null && (
            <Box sx={{ mb: 4 }}>
              <Tabs
                value={effectiveLocationId}
                onChange={(_e, value: string) => setActiveLocationId(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                aria-label="Schedule location"
              >
                {assignedLocations.map((loc) => (
                  <Tab key={loc.id} value={loc.id} label={loc.name === '' ? loc.id : loc.name} />
                ))}
              </Tabs>

              {schedule.status === 'loading' && (
                <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3, maxWidth: 640 }} />
              )}

              {schedule.status === 'error' && (
                <ScreenError
                  error={schedule.error}
                  what="the schedule for this location"
                  onRetry={schedule.refetch}
                  sx={{ maxWidth: 620, alignItems: 'center' }}
                />
              )}

              {schedule.status === 'ready' && schedule.data && (
                <ResourceScheduleGrid
                  // Remount per location / per (re)load so the rows reseed.
                  key={`sched|${effectiveLocationId}|${schedule.data.days
                    .map(
                      (d) =>
                        `${d.dayOfWeek}:${
                          d.isAvailable
                            ? d.shifts.map((s) => `${s.startTime}-${s.endTime}`).join(',')
                            : 'x'
                        }`,
                    )
                    .join('|')}`}
                  days={schedule.data.days}
                  resourceId={resourceId}
                  locationId={effectiveLocationId}
                  refetch={schedule.refetch}
                  showToast={showToast}
                />
              )}
            </Box>
          )}

          {/* ---- Date exceptions ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Date exceptions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Mark this resource off on a specific date, or set special hours — across all
            locations or one.
          </Typography>
          {exceptions.status === 'loading' && (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3, maxWidth: 640, mb: 4 }} />
          )}
          {exceptions.status === 'error' && (
            <ScreenError
              error={exceptions.error}
              what="the date exceptions"
              onRetry={exceptions.refetch}
              sx={{ maxWidth: 620, mb: 4, alignItems: 'center' }}
            />
          )}
          {exceptions.status === 'ready' && exceptions.data && (
            <Box sx={{ mb: 4 }}>
              <ResourceDateExceptionsPanel
                exceptions={exceptions.data}
                resourceId={resourceId}
                locations={assignedLocations}
                refetch={exceptions.refetch}
                showToast={showToast}
              />
            </Box>
          )}

          <ResourceDialog
            open={editOpen}
            resource={resource.data}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              resource.refetch();
              assigned.refetch();
            }}
            showToast={showToast}
          />
        </>
      )}
    </Box>
  );
}
